<template>
  <div>
    <div class="container">
      <div class="container">

        <!-- PAGE HEADER -->
        <div class="section-head" :style="{ '--accent': '#4F7EB3' }">
          <div class="head-left">
            <h3 class="title">{{ $t('cameras.newFlow.title') }}</h3>
          </div>
          <div class="head-actions">
            <button type="button" class="btn-outline" @click="cancelNewFlow">
              <i class="bi bi-x-square"></i>
              {{ $t('cameras.actions.cancel') }}
            </button>
          </div>
        </div>

        <!-- FORM CARD -->
        <div class="card form-card kpi-accent" :style="{ '--accent': '#4F7EB3' }">
          <div class="card-header form-head">
            <h4 class="form-title">{{ FlowName || $t('cameras.newFlow.name') }}</h4>
          </div>

          <div class="card-body">
            <div class="form-wrap">
              <!-- Name -->
              <div class="form-row">
                <label class="form-label" for="name">{{ $t('cameras.newFlow.name') }}</label>
                <input id="name" type="text" class="input" v-model="FlowName" :placeholder="$t('cameras.newFlow.namePlaceholder')" />
              </div>

              <!-- Camera settings (child) -->
              <div class="form-row" v-if="InputTypeId==='Camera Stream'">
                <camera-stream-settings
                  @streamlink="on_streamlink"
                  @capturedframe="on_capturedframe"
                />
              </div>
            </div>

            <!-- Detection zone editor (child) -->
            <div class="dz-wrap" v-if="InputTypeId==='Camera Stream' && StreamLink && capturedFrame">
              <detection-zone
                :stream_link="StreamLink"
                :captured_frame="capturedFrame"
                @detectionzones="on_detectionzone_logic"
              />
            </div>
          </div>

          <div class="form-actions">
            <div v-if="isSaving" class="saving hide-on-small">
              <div class="saver"></div><span>{{ $t('cameras.actions.saving') }}</span>
            </div>

            <button
              type="button"
              class="btn-solid ok hide-on-small"
              :class="{ 'is-disabled': isSaveButtonDisabled }"
              :disabled="isSaveButtonDisabled"
              @click="createFlow"
            >
              <i class="bi bi-save"></i> {{ $t('cameras.actions.save') }}
            </button>

            <button type="button" class="btn-outline" @click="cancelNewFlow">
              <i class="bi bi-x-square"></i> {{ $t('cameras.actions.cancel') }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { useFlowStore } from '@/stores/FlowStore'
import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'

import DropdownInputType from './DropdownInputType.vue'

import CameraStreamSettings from './CameraStreamSettings.vue'

import DetectionZone from './DetectionZone.vue'

export default {
    components: {
        DropdownInputType,
        CameraStreamSettings,
        DetectionZone,
    },

    setup(){
        const toast = useToast()
        const { t } = useI18n()

        const flowStore = useFlowStore()

        const camerastreams =   useCameraStreamStore()
        const detectionzones = useDetectionZoneStore()

        const router = useRouter()

        //Set variables
        const FlowName = ref('')
        const FlowId = ref(0)
        const InputTypeId = ref('Camera Stream') //Now fixed value. Potantial in the furture add other input streams (e.g. sensordata)
        const UserId = ref('')

        const StreamLink = ref('')
        const capturedFrame = ref(null)
        const DetectionZone = ref({})

        const isSaving = ref(null)


        //Update values when values in child are changed
        const on_inputtype_input = (value) => {
            InputTypeId.value = value
        }

        //Update values when values in child are changed
        const on_streamlink = (value) => {
            StreamLink.value = value
        }

        //Update values when values in child are changed
        const on_capturedframe = (value) => {
            capturedFrame.value = value
        }

        //Set values to push to child
        const on_detectionzone_logic = (value) => {
            DetectionZone.value = value
        }

        const resetValues = () => {
            FlowId.value = 0
            FlowName.value = ''
            InputTypeId.value = ''
            StreamLink.value = ''
            DetectionZone.value = []
        }
        
        const isSaveButtonDisabled = computed (() => {
            return (
                !FlowName.value ||
                !InputTypeId.value === 0 ||
                !StreamLink.value
            )
        })

        const createFlow = async () => {
          try {    
            isSaving.value = true

            if (InputTypeId.value === 'Camera Stream' && StreamLink.value !== '') {
              try {
                // 1) Create flow -> get new FlowId
                FlowId.value = await flowStore.createFlow(FlowName.value, InputTypeId.value)

                // 2) Attach camera stream
                try {
                  await camerastreams.addInputCameraStreamToFlow(FlowId.value, StreamLink.value)
                } catch (err) {
                  console.error("Error updating CameraStreams table:", err)
                  toast.error(t('cameras.newFlow.streamSaveError'))
                }

                // 3) Save detection zones if provided
                if (Object.keys(DetectionZone.value).length > 0) {
                  try {
                    await detectionzones.addDetectionZoneToFlow(FlowId.value, DetectionZone.value)
                  } catch (err) {
                    console.error("Error updating DetectionZone table:", err)
                    toast.error(t('cameras.newFlow.zonesSaveError'))
                  }
                }

                // 4) Clear caches, refresh list, toast, and route to the new flow page
                localStorage.removeItem('listOfFlows')
                localStorage.removeItem('FixedWidgetsData')
                localStorage.removeItem('detectionZoneData')

                await flowStore.fetchFlows()
                toast.success(t('cameras.newFlow.added'))

                // 🔹 Route to the specific flow output
                router.push(`/flow/${FlowId.value}`)

                // (Optional) reset after navigation
                resetValues()
                isSaving.value = false

              } catch (err) {
                console.error("Error creating flow:", err)
                toast.error(t('cameras.newFlow.flowSaveError'))
                isSaving.value = false
              }

            } else if (InputTypeId.value === 'Camera Stream' && StreamLink.value === '') {
              console.error("Stream link is empty.")
              toast.error(t('cameras.newFlow.linkMissing'))
              isSaving.value = false
            } else {
              console.error("Unexpected data combination when creating a flow.")
              toast.error(t('cameras.newFlow.dataError'))
              isSaving.value = false
            }

          } catch (err) {
            console.error("Unexpected error while writing data:", err)
            toast.error(t('cameras.newFlow.saveError'))
            isSaving.value = false
          }
        }

        const cancelNewFlow = () => {
            if (confirm(t('cameras.newFlow.confirmCancel'))) {
                isSaving.value = true
                resetValues()
                setTimeout(() => {
                    isSaving.value = false
                    router.push('/camera_overview')
                }, 100) // Add a slight delay before navigation
            }
        }

        const onCancel = () => {
                console.log('User cancelled the loader.')
        }

        return {
            FlowName,
            InputTypeId,
            UserId,
            StreamLink,
            flowStore,
            createFlow,
            on_inputtype_input,
            on_streamlink,
            on_capturedframe,
            capturedFrame,
            on_detectionzone_logic,
            cancelNewFlow,
            isSaveButtonDisabled,
            isSaving,
            onCancel
        }
    }
}
</script>

<style scoped>
/* --- Section header --- */
.section-head{
  display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;
  border-left:6px solid var(--accent,#4F7EB3);
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:12px 14px; margin:10px 0 14px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.title{ margin:0; font-weight:800; color:#425461 }
.head-actions{ display:flex; gap:10px; flex-wrap:wrap }

/* --- Buttons --- */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; background:#fff;
  padding:8px 12px; border-radius:10px; cursor:pointer; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }

.btn-solid{
  display:inline-flex; align-items:center; gap:8px;
  border:0; color:#fff; padding:8px 14px; border-radius:10px; cursor:pointer;
}
.btn-solid.ok{ background:#6C9046 }
.btn-solid.ok.is-disabled,
.btn-solid.ok:disabled{ opacity:.6; cursor:not-allowed }

/* --- Card + form --- */
.form-card{ border:none; box-shadow:0 4px 18px rgba(13,38,76,.06); border-radius:14px }
.kpi-accent{ position:relative }
.kpi-accent::before{
  content:""; position:absolute; inset:0 auto 0 0; width:6px; background:var(--accent,#4F7EB3);
  border-radius:14px 0 0 14px;
}

.form-head{
  display:flex; align-items:center; justify-content:space-between; gap:8px; padding:12px 16px;
  border-bottom:1px solid #eef3f7;
}
.form-title{ margin:0; color:#4F7EB3; font-weight:800 }

.card-body{ padding:14px 16px }
.form-wrap{ max-width:860px }
.form-row{ display:flex; flex-direction:column; gap:6px; margin-bottom:14px }
.form-label{ color:#425461; font-weight:700 }

.input{
  width:100%; border:1px solid #dfe7ee; border-radius:10px; padding:10px 12px;
  background:#fff; color:#2b3a44; outline:none;
}
.input:focus{ border-color:#4F7EB3; box-shadow:0 0 0 3px rgba(79,126,179,.12) }

.dz-wrap{ margin-top:16px }

/* footer */
.form-actions{
  display:flex; align-items:center; gap:10px; justify-content:flex-end;
  padding:12px 16px; border-top:1px solid #eef3f7;
}
.saving{ display:flex; align-items:center; gap:8px; margin-right:auto; color:#6b7a86 }

/* Save hidden on small screens — zone drawing/adding is hidden there too. */
@media (max-width: 1024px){
  .hide-on-small{ display:none !important; }
}

/* loader */
.saver{
  width:22px; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }
</style>